// src/pages/partners/partners-logged-in/client.tsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  Autocomplete,
  MenuItem,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import NutritionDaysView from "./nutrition";
import ExerciseList from "./exercise";
import {
  User,
  Workout,
  MealPlans as MealPlanType,
  Recipe,
} from "../../../models/types";
import GlassContainer from "../components/liquid-glass";
import { tokens } from "../components/design-tokens";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  revokeClientPro,
  updateClientGoals,
  updateClientMealPlans,
} from "../../../redux/thunks/thunk-partners";

// shared date range control
import DateRangeControls, {
  DateRange,
  getRangeForPreset,
} from "./shared/date-range-controls";
import {
  API_URL_recipe,
  getFreshIdToken,
} from "../../../redux/thunks/thunk-recipes";

interface ClientProps {
  client: User | null;
  clients?: User[];
  onSelectClient?: (client: User) => void;
  onBackToList?: () => void;
  onRemoved?: () => void;
}

type SortField = "name" | "gender" | "age" | "height" | "weight";
type SortDirection = "asc" | "desc";

export default function Client({
  client,
  clients = [],
  onSelectClient,
  onBackToList,
}: ClientProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ─────────────────────────────────────────────
  // LOCAL CLIENT LIST STATE (for when no client is selected)
  // ─────────────────────────────────────────────
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filters, setFilters] = useState<{
    name: string;
    gender: string;
    age: string;
    height: string;
    weight: string;
  }>({
    name: "",
    gender: "",
    age: "",
    height: "",
    weight: "",
  });

  const computeAge = (birthday?: string | Date | null): number | null => {
    if (!birthday) return null;
    const dob =
      typeof birthday === "string" ? new Date(birthday) : (birthday as Date);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
    return age;
  };

  const getLatestWeight = (u: User): number | null => {
    const weightVal = Array.isArray(u.weight)
      ? u.weight[u.weight.length - 1]
      : (u.weight as number | undefined | null);
    if (weightVal == null) return null;
    return u.isMetricUnits ? weightVal : weightVal * 2.20462;
  };

  const processedClients = useMemo(() => {
    const mapped = (clients || []).map((u) => {
      const age = computeAge(u.birthday as any);
      const latestWeight = getLatestWeight(u);
      const heightVal =
        typeof u.height === "number" && !Number.isNaN(u.height)
          ? Math.round(u.height)
          : null;

      const displayHeight = heightVal != null ? `${heightVal} cm` : "—";
      const displayWeight =
        latestWeight != null
          ? `${Math.round(latestWeight)} ${u.isMetricUnits ? "kg" : "lbs"}`
          : "—";

      return {
        original: u,
        name: u.name || "",
        gender: u.gender || "",
        age,
        height: heightVal,
        weight: latestWeight,
        displayHeight,
        displayWeight,
      };
    });

    const filtered = mapped.filter((row) => {
      const checks: boolean[] = [];

      if (filters.name) {
        checks.push(
          row.name.toLowerCase().includes(filters.name.toLowerCase())
        );
      }
      if (filters.gender) {
        checks.push(
          row.gender.toLowerCase().includes(filters.gender.toLowerCase())
        );
      }
      if (filters.age) {
        checks.push(
          (row.age != null ? String(row.age) : "")
            .toLowerCase()
            .includes(filters.age.toLowerCase())
        );
      }
      if (filters.height) {
        checks.push(
          row.displayHeight.toLowerCase().includes(filters.height.toLowerCase())
        );
      }
      if (filters.weight) {
        checks.push(
          row.displayWeight.toLowerCase().includes(filters.weight.toLowerCase())
        );
      }

      return checks.every(Boolean);
    });

    const sorted = [...filtered].sort((a, b) => {
      let av: any = a[sortField];
      let bv: any = b[sortField];

      if (av == null && bv == null) return 0;
      if (av == null) return sortDirection === "asc" ? 1 : -1;
      if (bv == null) return sortDirection === "asc" ? -1 : 1;

      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();

      if (av < bv) return sortDirection === "asc" ? -1 : 1;
      if (av > bv) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [clients, filters, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return field;
    });
  };

  const handleFilterChange =
    (field: keyof typeof filters) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilters((prev) => ({ ...prev, [field]: value }));
      };

  // ─────────────────────────────────────────────
  // DETAIL VIEW STATE (for a selected client)
  // ─────────────────────────────────────────────
  const [selectedTab, setSelectedTab] = useState<"Nutrition" | "Exercise">(
    "Nutrition"
  );
  const [currentScrollMonth, setCurrentScrollMonth] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [removeClientModal, showRemoveClientModal] = useState(false);
  const dispatch = useDispatch<any>();

  const trainerUserId: string | undefined = useSelector(
    (s: any) => s?.partners?.partnerData?.partnerData?.userId
  );

  // trainer's meal plan library from Redux (partners slice)
  const trainerMealPlans: MealPlanType[] = useSelector(
    (s: any) => s?.partners?.mealPlans ?? []
  );

  // REMOVE CLIENT FLOW
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // date range (default: past 7 days)
  const [range, setRange] = useState<DateRange>(() => getRangeForPreset("P7"));

  useEffect(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const dividers =
        scrollContainerRef.current.querySelectorAll(".meal-divider");
      let current = "";
      const containerTop =
        scrollContainerRef.current.getBoundingClientRect().top;
      dividers.forEach((divider) => {
        const rect = (divider as HTMLElement).getBoundingClientRect();
        if (rect.top - containerTop <= 50)
          current = (divider as HTMLElement).innerText;
      });
      setCurrentScrollMonth(current);
    }
  };

  const safeClient = (client ?? ({} as User)) as User;

  const getTotalWorkoutDurationLast7Days = (workouts: Workout[]): number => {
    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);
    return workouts
      .filter((w) => new Date(w.dayOfExecution) >= sevenDaysAgo)
      .reduce((sum, w) => sum + w.durationInMinutes, 0);
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const totalWorkoutDuration = formatDuration(
    getTotalWorkoutDurationLast7Days(safeClient.workouts || [])
  );

  // latest weight
  const weightVal = Array.isArray(safeClient.weight)
    ? safeClient.weight[safeClient.weight.length - 1]
    : (safeClient.weight as number);

  // ─────────────────────────────────────────────
  // NUTRITION MACRO GOAL EDITING
  // ─────────────────────────────────────────────
  const initialProtein = safeClient.goals?.proteinGoal ?? 0;
  const initialCarbs = safeClient.goals?.carbsGoal ?? 0;
  const initialFats = safeClient.goals?.fatsGoal ?? 0;

  // we keep a LOCAL baseline so after "Save" the button goes away
  const [macroBaseline, setMacroBaseline] = useState<{
    protein: number;
    carbs: number;
    fats: number;
    calories: number;
  }>({
    protein: initialProtein,
    carbs: initialCarbs,
    fats: initialFats,
    calories:
      (initialProtein || 0) * 4 +
      (initialCarbs || 0) * 4 +
      (initialFats || 0) * 9,
  });

  const computeCaloriesFromMacros = (
    protein: number,
    carbs: number,
    fats: number
  ) => protein * 4 + carbs * 4 + fats * 9;

  const [macroDraftProtein, setMacroDraftProtein] =
    useState<number>(initialProtein);
  const [macroDraftCarbs, setMacroDraftCarbs] = useState<number>(initialCarbs);
  const [macroDraftFats, setMacroDraftFats] = useState<number>(initialFats);

  const [isEditingMacros, setIsEditingMacros] = useState(false);
  const [isSavingMacros, setIsSavingMacros] = useState(false);

  const derivedCalories = useMemo(
    () =>
      computeCaloriesFromMacros(
        macroDraftProtein || 0,
        macroDraftCarbs || 0,
        macroDraftFats || 0
      ),
    [macroDraftProtein, macroDraftCarbs, macroDraftFats]
  );

  const macrosDirty =
    derivedCalories !== macroBaseline.calories ||
    macroDraftProtein !== macroBaseline.protein ||
    macroDraftCarbs !== macroBaseline.carbs ||
    macroDraftFats !== macroBaseline.fats;

  const handleSaveMacros = async () => {
    if (!safeClient.userId) return;
    setIsSavingMacros(true);
    try {
      await dispatch(
        updateClientGoals({
          clientUserId: safeClient.userId,
          proteinGoal: macroDraftProtein || 0,
          carbsGoal: macroDraftCarbs || 0,
          fatsGoal: macroDraftFats || 0,
          caloriesGoal: derivedCalories || 0,
        })
      );
      setMacroBaseline({
        protein: macroDraftProtein || 0,
        carbs: macroDraftCarbs || 0,
        fats: macroDraftFats || 0,
        calories: derivedCalories || 0,
      });
      setIsEditingMacros(false);
    } finally {
      setIsSavingMacros(false);
    }
  };

  // ─────────────────────────────────────────────
  // CLIENT MEAL PLANS (MULTIPLE)
  // ─────────────────────────────────────────────
  const initialPlanIdsFromClient: string[] = useMemo(
    () => (safeClient.mealPlans ?? []).map((p) => p.id),
    [safeClient.mealPlans]
  );
  const [baselinePlanIds, setBaselinePlanIds] = useState<string[]>(
    initialPlanIdsFromClient
  );

  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>(
    initialPlanIdsFromClient
  );

  const [isEditingMeals, setIsEditingMeals] = useState(false);
  const [isSavingMeals, setIsSavingMeals] = useState(false);

  const arraysEqualAsSets = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sa = new Set(a);
    for (const x of b) if (!sa.has(x)) return false;
    return true;
  };

  const plansDirty = !arraysEqualAsSets(selectedPlanIds, baselinePlanIds);

  const selectedPlansFull: MealPlanType[] = useMemo(() => {
    const sel = new Set(selectedPlanIds);
    return trainerMealPlans.filter((p) => sel.has(p.id));
  }, [selectedPlanIds, trainerMealPlans]);

  const handleSavePlans = async () => {
    if (!safeClient.userId) return;
    setIsSavingMeals(true);
    try {
      const idToken = await getFreshIdToken();

      // 1. Fetch trainer recipes (fresh, not from Redux)
      const trainerRes = await fetch(
        `${API_URL_recipe}?userId=${trainerUserId}`,
        {
          headers: { Authorization: idToken || "" },
        }
      );
      const trainerData = await trainerRes.json();
      const trainerRecipes = trainerData.recipes || [];

      // 2. Fetch client recipes
      const clientRes = await fetch(
        `${API_URL_recipe}?userId=${safeClient.userId}`,
        {
          headers: { Authorization: idToken || "" },
        }
      );
      const clientData = await clientRes.json();
      const clientRecipes = clientData.recipes || [];
      const clientExistingIds = new Set(
        clientRecipes.map((r: Recipe) => r.recipeId)
      );

      // 3. Determine which trainer recipes belong to selected plans
      const trainerRefs = new Set(
        selectedPlansFull.flatMap((p) =>
          (p.recipes ?? []).map((r) => r.recipesId)
        )
      );
      const toClone = trainerRecipes.filter((r: Recipe) =>
        trainerRefs.has(r.recipeId)
      );

      // 4. Clone them under the client’s userId
      const clones = toClone.map((r: Recipe) => {
        let finalId = r.recipeId;
        while (clientExistingIds.has(finalId))
          finalId = `${r.recipeId}#${Math.random().toString(36).slice(2, 6)}`;
        clientExistingIds.add(finalId);
        return {
          ...r,
          userId: safeClient.userId,
          recipeId: finalId,
          originUserId: r.userId,
          originRecipeId: r.recipeId,
          isSnapshot: true,
        };
      });

      // 5. Bulk create cloned recipes
      if (clones.length) {
        await fetch(API_URL_recipe, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: idToken || "",
          },
          body: JSON.stringify({
            userId: safeClient.userId,
            recipes: clones,
          }),
        });
      }

      // build lookup of oldId -> newId before POST
      const idMap = new Map<string, string>();
      clones.forEach((c: Recipe, i: number) => {
        const original = toClone[i]?.recipeId;
        if (original) idMap.set(original, c.recipeId);
      });

      // 6. Persist updated meal plans with cloned recipeIds
      const rewrittenPlans = selectedPlansFull.map((p) => ({
        ...p,
        recipes: (p.recipes ?? []).map((r: any) => ({
          ...r,
          userId: safeClient.userId,
          recipeId: idMap.get(r.recipeId) ?? r.recipeId, // swap if cloned, else keep
        })),
        assignedToUserId: safeClient.userId,
      }));

      await dispatch(
        updateClientMealPlans({
          clientUserId: safeClient.userId,
          mealPlans: rewrittenPlans,
        })
      );

      setBaselinePlanIds(selectedPlanIds);
      setIsEditingMeals(false);
    } finally {
      setIsSavingMeals(false);
    }
  };

  const handleRemoveOnePlan = (planId: string) => {
    setSelectedPlanIds((prev) => prev.filter((id) => id !== planId));
  };

  // ─────────────────────────────────────────────
  // WATER GOAL (single)
  // ─────────────────────────────────────────────
  const initialWaterGoal = safeClient.goals?.waterGoal ?? {
    units: "oz" as "oz" | "ml",
    amount: 0,
  };

  const [waterGoalBaseline, setWaterGoalBaseline] = useState<{
    units: "ml" | "oz";
    amount: number;
  }>(initialWaterGoal);

  const [waterAmount, setWaterAmount] = useState<number>(
    initialWaterGoal.amount || 0
  );
  const [waterUnits, setWaterUnits] = useState<"ml" | "oz">(
    initialWaterGoal.units || "oz"
  );
  const [isEditingWater, setIsEditingWater] = useState(false);
  const [isSavingWater, setIsSavingWater] = useState(false);

  const waterDirty =
    waterAmount !== waterGoalBaseline.amount ||
    waterUnits !== waterGoalBaseline.units;

  const handleSaveWater = async () => {
    if (!safeClient.userId) return;
    setIsSavingWater(true);
    try {
      await dispatch(
        updateClientGoals({
          clientUserId: safeClient.userId,
          waterGoal: { units: waterUnits, amount: waterAmount },
        })
      );
      setWaterGoalBaseline({ units: waterUnits, amount: waterAmount });
      setIsEditingWater(false);
    } finally {
      setIsSavingWater(false);
    }
  };

  // ─────────────────────────────────────────────
  // SUPPLEMENT GOALS (multiple)
  // ─────────────────────────────────────────────
  const initialSupps =
    safeClient.goals?.supplementGoal?.map((s) => ({ ...s })) ?? [];

  const [suppBaseline, setSuppBaseline] = useState<
    {
      supplementName: string;
      units: string;
      notes: string;
      quantity: string;
      recurring: {
        mon: boolean;
        tue: boolean;
        wed: boolean;
        thu: boolean;
        fri: boolean;
        sat: boolean;
        sun: boolean;
      };
    }[]
  >(initialSupps);

  const [suppDrafts, setSuppDrafts] =
    useState<typeof suppBaseline>(initialSupps);

  const [isEditingSupps, setIsEditingSupps] = useState(false);
  const [isSavingSupps, setIsSavingSupps] = useState(false);

  const suppsDirty = (() => {
    if (suppDrafts.length !== suppBaseline.length) return true;
    // naive deep compare
    for (let i = 0; i < suppDrafts.length; i++) {
      const d = suppDrafts[i];
      const b = suppBaseline[i];
      const same =
        d.supplementName === b.supplementName &&
        d.units === b.units &&
        d.notes === b.notes &&
        d.quantity === b.quantity &&
        d.recurring.mon === b.recurring.mon &&
        d.recurring.tue === b.recurring.tue &&
        d.recurring.wed === b.recurring.wed &&
        d.recurring.thu === b.recurring.thu &&
        d.recurring.fri === b.recurring.fri &&
        d.recurring.sat === b.recurring.sat &&
        d.recurring.sun === b.recurring.sun;
      if (!same) return true;
    }
    return false;
  })();

  const handleAddSupplement = () => {
    setSuppDrafts((prev) => [
      ...prev,
      {
        supplementName: "",
        units: "",
        notes: "",
        quantity: "",
        recurring: {
          mon: true,
          tue: true,
          wed: true,
          thu: true,
          fri: true,
          sat: false,
          sun: false,
        },
      },
    ]);
  };

  const handleUpdateSupp = (
    idx: number,
    field: "supplementName" | "units" | "notes" | "quantity" | "recurring",
    value: any
  ) => {
    setSuppDrafts((prev) =>
      prev.map((s, i) =>
        i === idx
          ? field === "recurring"
            ? { ...s, recurring: { ...s.recurring, ...value } }
            : { ...s, [field]: value }
          : s
      )
    );
  };

  const handleDeleteSupp = (idx: number) => {
    setSuppDrafts((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveSupps = async () => {
    if (!safeClient.userId) return;
    setIsSavingSupps(true);
    try {
      await dispatch(
        updateClientGoals({
          clientUserId: safeClient.userId,
          supplementGoal: suppDrafts,
        })
      );
      setSuppBaseline(suppDrafts);
      setIsEditingSupps(false);
    } finally {
      setIsSavingSupps(false);
    }
  };

  // ─────────────────────────────────────────────
  // REMOVE CLIENT FLOW
  // ─────────────────────────────────────────────
  const handleConfirmRemove = async () => {
    setRemoveError(null);
    if (!trainerUserId) {
      setRemoveError("Missing trainer ID. Please reload and try again.");
      return;
    }
    if (!safeClient.userId) {
      setRemoveError("Missing client ID.");
      return;
    }
    setRemoving(true);
    try {
      await dispatch(
        revokeClientPro({ client: safeClient.userId, trainerUserId })
      ).unwrap();
      showRemoveClientModal(false);
    } catch (err: any) {
      setRemoveError(err?.message || "Failed to remove client.");
    } finally {
      setRemoving(false);
    }
  };

  // ─────────────────────────────────────────────
  // LIST VIEW (NO CLIENT SELECTED)
  // ─────────────────────────────────────────────
  if (!client || !safeClient.userId) {
    return (
      <Box
        sx={{
          p: 2,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Clients
        </Typography>
        <Paper
          sx={{
            flex: 1,
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "#ffffff",
          }}
        >
          {processedClients.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.7,
                }}
              >
                No clients yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {/* Name */}
                    <TableCell
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "name"}
                          direction={
                            sortField === "name" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("name")}
                        >
                          Name
                        </TableSortLabel>
                        <TextField
                          variant="standard"
                          value={filters.name}
                          onChange={handleFilterChange("name")}
                          placeholder="Filter"
                          InputProps={{
                            sx: { fontSize: 12 },
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* Gender */}
                    <TableCell
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "gender"}
                          direction={
                            sortField === "gender" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("gender")}
                        >
                          Gender
                        </TableSortLabel>
                        <TextField
                          variant="standard"
                          value={filters.gender}
                          onChange={handleFilterChange("gender")}
                          placeholder="Filter"
                          InputProps={{
                            sx: { fontSize: 12 },
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* Age */}
                    <TableCell
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "age"}
                          direction={
                            sortField === "age" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("age")}
                        >
                          Age
                        </TableSortLabel>
                        <TextField
                          variant="standard"
                          value={filters.age}
                          onChange={handleFilterChange("age")}
                          placeholder="Filter"
                          InputProps={{
                            sx: { fontSize: 12 },
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* Height */}
                    <TableCell
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "height"}
                          direction={
                            sortField === "height" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("height")}
                        >
                          Height
                        </TableSortLabel>
                        <TextField
                          variant="standard"
                          value={filters.height}
                          onChange={handleFilterChange("height")}
                          placeholder="Filter"
                          InputProps={{
                            sx: { fontSize: 12 },
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* Weight */}
                    <TableCell
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "weight"}
                          direction={
                            sortField === "weight" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("weight")}
                        >
                          Weight
                        </TableSortLabel>
                        <TextField
                          variant="standard"
                          value={filters.weight}
                          onChange={handleFilterChange("weight")}
                          placeholder="Filter"
                          InputProps={{
                            sx: { fontSize: 12 },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedClients.map((row) => (
                    <TableRow
                      key={row.original.userId}
                      hover
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        onSelectClient && onSelectClient(row.original)
                      }
                    >
                      <TableCell>{row.name || "—"}</TableCell>
                      <TableCell>{row.gender || "—"}</TableCell>
                      <TableCell>{row.age != null ? row.age : "—"}</TableCell>
                      <TableCell>{row.displayHeight}</TableCell>
                      <TableCell>{row.displayWeight}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    );
  }

  // ─────────────────────────────────────────────
  // DETAIL VIEW (CLIENT SELECTED)
  // ─────────────────────────────────────────────
  return (
    <>
      <Box
        p={2}
        sx={{
          overflowY: "auto",
          maxHeight: "100vh",
          width: "100%",
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
          " ::-webkit-scrollbar ": { display: "none" },
          scrollbarWidth: 0,
          // borderRadius: 8,
        }}
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {/* HEADER CARD */}
        <GlassContainer
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 4,
            display: "flex",
            justifyContent: "space-between",
            "&:hover": { transform: "scale(1.0)" },
          }}
        >
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems="center"
            sx={{ width: "100%" }}
          >
            {onBackToList && (
              <IconButton
                onClick={onBackToList}
                sx={{
                  mr: 2,
                  borderRadius: "50%",
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.2)",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(255,255,255,0.85)",
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
            )}

            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              sx={{ width: "100%" }}
            >
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width={360}
                flex={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h2">{safeClient.name}</Typography>
                </Box>

                {/* Tab Switch */}
                <Paper
                  sx={{
                    borderRadius: 16,
                    width: "fit-content",
                    display: "flex",
                    justifyContent: "center",
                    boxShadow: "none",
                    gap: 2,
                    backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    p: 0.5,
                  }}
                >
                  <Button
                    variant={
                      selectedTab === "Nutrition" ? "contained" : "outlined"
                    }
                    onClick={() => setSelectedTab("Nutrition")}
                    sx={{
                      borderColor: "transparent",
                      textTransform: "none",
                      borderRadius: 16,
                      backgroundColor:
                        selectedTab === "Nutrition"
                          ? (isDark ? "rgba(255,255,255,0.15)" : "white")
                          : "transparent",
                    }}
                  >
                    <Typography
                      color={tokens.text.primary(selectedTab === "Nutrition" ? !isDark : isDark)}
                    >
                      Nutrition
                    </Typography>
                  </Button>
                  <Button
                    variant={
                      selectedTab === "Exercise" ? "contained" : "outlined"
                    }
                    onClick={() => setSelectedTab("Exercise")}
                    sx={{
                      borderColor: "transparent",
                      textTransform: "none",
                      borderRadius: 16,
                      backgroundColor:
                        selectedTab === "Exercise"
                          ? (isDark ? "rgba(255,255,255,0.15)" : "white")
                          : "transparent",
                    }}
                  >
                    <Typography
                      color={tokens.text.primary(selectedTab === "Exercise" ? !isDark : isDark)}
                    >
                      Exercise
                    </Typography>
                  </Button>
                </Paper>

                {/* Date range control */}
                <DateRangeControls value={range} onChange={setRange} />
              </Box>

              {/* Right Column: stats + remove */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  maxWidth: "fit-content",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 1,
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    maxWidth={248}
                    marginRight={3}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: "text.primary", fontWeight: "600", fontSize: 20 }}
                    >
                      Height
                    </Typography>
                    <Typography variant="body2" fontSize={16}>
                      {Math.round(safeClient.height)} cm
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    maxWidth={248}
                    marginRight={3}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: "text.primary", fontWeight: "600", fontSize: 20 }}
                    >
                      Gender
                    </Typography>
                    <Typography variant="body2" fontSize={16}>
                      {safeClient.gender}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    maxWidth={248}
                    marginRight={3}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: "text.primary", fontWeight: "600", fontSize: 20 }}
                    >
                      Weight
                    </Typography>
                    <Typography variant="body2" fontSize={16}>
                      {Math.round(
                        safeClient.isMetricUnits
                          ? weightVal ?? 0
                          : (weightVal ?? 0) * 2.20462
                      )}{" "}
                      {safeClient.isMetricUnits ? "kg" : "lbs"}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ alignSelf: "end" }}>
                  <Button
                    sx={{ borderRadius: 16, textTransform: "none" }}
                    onClick={() => showRemoveClientModal(true)}
                  >
                    <Typography sx={{ color: "red" }}>Remove Client</Typography>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </GlassContainer>

        {/* NUTRITION CONTROL PANEL */}
        {selectedTab === "Nutrition" && (
          <GlassContainer
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 4,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            {/* Column 1: Macro Goals + Water Goal */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Macro Goals */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Macro Goals
                  </Typography>
                  <Button
                    onClick={() => {
                      if (isEditingMacros) {
                        // cancel -> revert to baseline
                        setMacroDraftProtein(macroBaseline.protein);
                        setMacroDraftCarbs(macroBaseline.carbs);
                        setMacroDraftFats(macroBaseline.fats);
                        setIsEditingMacros(false);
                      } else {
                        setIsEditingMacros(true);
                      }
                    }}
                    size="small"
                    startIcon={
                      <EditIcon fontSize="small" sx={{ color: "text.primary" }} />
                    }
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      gap: 0.5,
                      color: "text.primary",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      {isEditingMacros ? "Cancel" : "Edit"}
                    </Typography>
                  </Button>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    borderRadius: 3,
                    p: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: isDark ? "2px solid rgba(255,255,255,0.12)" : "2px solid rgba(0,0,0,0.08)",
                    backgroundColor: "transparent",
                    gap: 2,
                  }}
                >
                  {/* Protein */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 70,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      Protein (g)
                    </Typography>
                    {isEditingMacros ? (
                      <TextField
                        size="small"
                        type="number"
                        value={macroDraftProtein}
                        onChange={(e) =>
                          setMacroDraftProtein(Number(e.target.value) || 0)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            width: 90,
                          },
                        }}
                        inputProps={{
                          style: {
                            textAlign: "center",
                            paddingTop: 6,
                            paddingBottom: 6,
                          },
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontWeight: 600, lineHeight: "32px" }}>
                        {macroDraftProtein}
                      </Typography>
                    )}
                  </Box>

                  {/* Carbs */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 70,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      Carbs (g)
                    </Typography>
                    {isEditingMacros ? (
                      <TextField
                        size="small"
                        type="number"
                        value={macroDraftCarbs}
                        onChange={(e) =>
                          setMacroDraftCarbs(Number(e.target.value) || 0)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            width: 90,
                          },
                        }}
                        inputProps={{
                          style: {
                            textAlign: "center",
                            paddingTop: 6,
                            paddingBottom: 6,
                          },
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontWeight: 600, lineHeight: "32px" }}>
                        {macroDraftCarbs}
                      </Typography>
                    )}
                  </Box>

                  {/* Fats */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 70,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      Fats (g)
                    </Typography>
                    {isEditingMacros ? (
                      <TextField
                        size="small"
                        type="number"
                        value={macroDraftFats}
                        onChange={(e) =>
                          setMacroDraftFats(Number(e.target.value) || 0)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            width: 90,
                          },
                        }}
                        inputProps={{
                          style: {
                            textAlign: "center",
                            paddingTop: 6,
                            paddingBottom: 6,
                          },
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontWeight: 600, lineHeight: "32px" }}>
                        {macroDraftFats}
                      </Typography>
                    )}
                  </Box>

                  {/* Calories (derived, text-only) */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 100,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      Calories
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        textAlign: "center",
                        lineHeight: "32px",
                      }}
                    >
                      {derivedCalories}
                    </Typography>
                  </Box>

                  {/* Save Macros */}
                  {isEditingMacros && macrosDirty && (
                    <>
                      {isSavingMacros ? (
                        <Box
                          sx={{
                            ml: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={18} />
                          <Typography variant="body2">Saving…</Typography>
                        </Box>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleSaveMacros}
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                            color: "white",
                            ml: "auto",
                          }}
                        >
                          Save
                        </Button>
                      )}
                    </>
                  )}
                </Paper>
              </Box>

              {/* Water Goal */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Water Goal
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      if (isEditingWater) {
                        // cancel
                        setWaterAmount(waterGoalBaseline.amount);
                        setWaterUnits(waterGoalBaseline.units);
                        setIsEditingWater(false);
                      } else {
                        setIsEditingWater(true);
                      }
                    }}
                    startIcon={
                      <EditIcon fontSize="small" sx={{ color: "text.primary" }} />
                    }
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      gap: 0.5,
                      color: "text.primary",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      {isEditingWater ? "Cancel" : "Edit"}
                    </Typography>
                  </Button>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    borderRadius: 3,
                    p: 2,
                    height: 68,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    border: isDark ? "2px solid rgba(255,255,255,0.12)" : "2px solid rgba(0,0,0,0.08)",
                    backgroundColor: "transparent",
                  }}
                >
                  {isEditingWater ? (
                    <>
                      <TextField
                        size="small"
                        type="number"
                        label="Amount"
                        value={waterAmount}
                        onChange={(e) =>
                          setWaterAmount(Number(e.target.value) || 0)
                        }
                        sx={{ width: 100 }}
                      />
                      <TextField
                        size="small"
                        select
                        label="Units"
                        value={waterUnits}
                        onChange={(e) =>
                          setWaterUnits(e.target.value as "ml" | "oz")
                        }
                        sx={{ width: 100 }}
                      >
                        <MenuItem value="oz">oz</MenuItem>
                        <MenuItem value="ml">ml</MenuItem>
                      </TextField>
                      {waterDirty &&
                        (isSavingWater ? (
                          <Box
                            sx={{
                              ml: "auto",
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <CircularProgress size={18} />
                            <Typography variant="body2">Saving…</Typography>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleSaveWater}
                            sx={{
                              ml: "auto",
                              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                              textTransform: "none",
                              borderRadius: 2,
                            }}
                          >
                            Save
                          </Button>
                        ))}
                    </>
                  ) : (
                    <Typography sx={{ fontWeight: 600 }}>
                      {waterGoalBaseline.amount} {waterGoalBaseline.units} / day
                    </Typography>
                  )}
                </Paper>
              </Box>
            </Box>

            {/* Column 2: Meal Plans + Supplement Goals */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Meal Plans */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Meal Plans
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      if (isEditingMeals) {
                        // cancel -> revert
                        setSelectedPlanIds(baselinePlanIds);
                        setIsEditingMeals(false);
                      } else {
                        setIsEditingMeals(true);
                      }
                    }}
                    startIcon={
                      <EditIcon fontSize="small" sx={{ color: "text.primary" }} />
                    }
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      gap: 0.5,
                      color: "text.primary",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      {isEditingMeals ? "Cancel" : "Edit"}
                    </Typography>
                  </Button>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    borderRadius: 3,
                    height: 85,
                    p: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: isDark ? "2px solid rgba(255,255,255,0.12)" : "2px solid rgba(0,0,0,0.08)",
                    backgroundColor: "transparent",
                    gap: 2,
                  }}
                >
                  {isEditingMeals ? (
                    <>
                      <Autocomplete
                        multiple
                        options={trainerMealPlans}
                        disableCloseOnSelect
                        getOptionLabel={(plan) => plan.title || "Untitled Plan"}
                        value={trainerMealPlans.filter((p) =>
                          selectedPlanIds.includes(p.id)
                        )}
                        onChange={(_e, newPlans) =>
                          setSelectedPlanIds(newPlans.map((p) => p.id))
                        }
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.id}
                              label={option.title || "Untitled Plan"}
                              onDelete={() => handleRemoveOnePlan(option.id)}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select plans…"
                            sx={{
                              minWidth: 280,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                              },
                            }}
                          />
                        )}
                        sx={{ flex: 1, minWidth: 280, maxWidth: 560 }}
                      />
                      {plansDirty &&
                        (isSavingMeals ? (
                          <Box
                            sx={{
                              ml: "auto",
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <CircularProgress size={18} />
                            <Typography variant="body2">Saving…</Typography>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleSavePlans}
                            sx={{
                              borderRadius: "10px",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              px: 1.5,
                              py: 0.75,
                              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                              color: "white",
                              ml: "auto",
                            }}
                          >
                            Save Plans
                          </Button>
                        ))}
                    </>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedPlansFull.length === 0 ? (
                        <Typography sx={{ color: "text.secondary" }}>
                          No meal plans assigned
                        </Typography>
                      ) : (
                        selectedPlansFull.map((p) => (
                          <Chip
                            key={p.id}
                            label={p.title || "Untitled Plan"}
                            variant="outlined"
                            sx={{ color: "text.primary", borderColor: "text.primary" }}
                          />
                        ))
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>

              {/* Supplement Goals */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Supplement Goals
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      if (isEditingSupps) {
                        // cancel -> revert
                        setSuppDrafts(suppBaseline);
                        setIsEditingSupps(false);
                      } else {
                        setIsEditingSupps(true);
                      }
                    }}
                    startIcon={
                      <EditIcon fontSize="small" sx={{ color: "text.primary" }} />
                    }
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      gap: 0.5,
                      color: "text.primary",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      {isEditingSupps ? "Cancel" : "Edit"}
                    </Typography>
                  </Button>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    borderRadius: 3,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    border: isDark ? "2px solid rgba(255,255,255,0.12)" : "2px solid rgba(0,0,0,0.08)",
                    backgroundColor: "transparent",
                  }}
                >
                  {isEditingSupps ? (
                    <>
                      {suppDrafts.map((supp, idx) => (
                        <Paper
                          key={idx}
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            p: 1,
                            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.04)",
                            background: isDark ? "rgba(255,255,255,0.04)" : "white",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            <TextField
                              label="Supplement"
                              size="small"
                              value={supp.supplementName}
                              onChange={(e) =>
                                handleUpdateSupp(
                                  idx,
                                  "supplementName",
                                  e.target.value
                                )
                              }
                              sx={{ flex: 1 }}
                            />
                            <TextField
                              label="Qty"
                              size="small"
                              value={supp.quantity}
                              onChange={(e) =>
                                handleUpdateSupp(
                                  idx,
                                  "quantity",
                                  e.target.value
                                )
                              }
                              sx={{ width: 80 }}
                            />
                            <TextField
                              label="Units"
                              size="small"
                              value={supp.units}
                              onChange={(e) =>
                                handleUpdateSupp(idx, "units", e.target.value)
                              }
                              sx={{ width: 100 }}
                            />
                            <Button
                              variant="text"
                              color="error"
                              onClick={() => handleDeleteSupp(idx)}
                            >
                              Remove
                            </Button>
                          </Stack>
                          <TextField
                            label="Notes"
                            size="small"
                            fullWidth
                            value={supp.notes}
                            onChange={(e) =>
                              handleUpdateSupp(idx, "notes", e.target.value)
                            }
                            sx={{ mb: 1 }}
                          />
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            {(
                              [
                                ["mon", "Mon"],
                                ["tue", "Tue"],
                                ["wed", "Wed"],
                                ["thu", "Thu"],
                                ["fri", "Fri"],
                                ["sat", "Sat"],
                                ["sun", "Sun"],
                              ] as [keyof typeof supp.recurring, string][]
                            ).map(([key, label]) => (
                              <Chip
                                key={key}
                                label={label}
                                size="small"
                                color={
                                  supp.recurring[key] ? "primary" : "default"
                                }
                                onClick={() =>
                                  handleUpdateSupp(idx, "recurring", {
                                    [key]: !supp.recurring[key],
                                  })
                                }
                              />
                            ))}
                          </Box>
                        </Paper>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddSupplement}
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                      >
                        Add supplement
                      </Button>
                      {suppsDirty &&
                        (isSavingSupps ? (
                          <Box
                            sx={{
                              alignSelf: "flex-end",
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <CircularProgress size={18} />
                            <Typography variant="body2">Saving…</Typography>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleSaveSupps}
                            sx={{
                              alignSelf: "flex-end",
                              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                              textTransform: "none",
                              borderRadius: 2,
                            }}
                          >
                            Save Supplements
                          </Button>
                        ))}
                    </>
                  ) : suppBaseline.length === 0 ? (
                    <Typography sx={{ color: "text.secondary" }}>
                      No supplement goals assigned
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {suppBaseline.map((s, idx) => (
                        <Chip
                          key={idx}
                          label={`${s.supplementName} ${s.quantity
                            ? `• ${s.quantity}${s.units ? ` ${s.units}` : ""}`
                            : ""
                            }`}
                          variant="outlined"
                          sx={{ color: "text.primary", borderColor: "text.primary" }}
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          </GlassContainer>
        )}

        {/* Remove dialog */}
        <Dialog
          open={removeClientModal}
          onAbort={() => showRemoveClientModal(false)}
          sx={{ alignItems: "center" }}
        >
          <DialogTitle variant="h1" sx={{ paddingBottom: 2 }}>
            Remove Client
          </DialogTitle>
          <DialogContent sx={{ width: "100vh" }}>
            {removeError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {removeError}
              </Alert>
            )}
            <Typography variant="body2" sx={{ paddingBottom: 1 }}>
              Are you sure you want to remove this client?
            </Typography>
            <Typography variant="body2">
              {safeClient.name} will lose their Avro app PRO access.
            </Typography>
          </DialogContent>
          <Typography
            variant="caption"
            sx={{ paddingLeft: 3, marginTop: 2, marginBottom: 2 }}
          >
            <u>We recommend reaching out to let them know</u>
          </Typography>
          <DialogActions>
            <Button
              onClick={() => showRemoveClientModal(false)}
              sx={{ backgroundColor: "lightgray" }}
              disabled={removing}
            >
              <Typography sx={{ color: "white" }}>Cancel</Typography>
            </Button>
            <Button
              sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}
              onClick={handleConfirmRemove}
              disabled={removing || !trainerUserId}
            >
              {removing ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography sx={{ color: "white" }}>Removing…</Typography>
                </Box>
              ) : (
                <Typography sx={{ color: "white" }}>YES</Typography>
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Content Section */}
        {selectedTab === "Nutrition" ? (
          <NutritionDaysView
            meals={safeClient.meals ?? []}
            user={safeClient}
            dateRange={range}
          />
        ) : (
          <ExerciseList
            workouts={safeClient.workouts ?? []}
            isMetricUnits={!!safeClient.isMetricUnits}
            client={safeClient}
            dateRange={range}
          />
        )}
      </Box>

      {/* floating month label on scroll */}
      {currentScrollMonth && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            right: "10px",
            transform: "translateY(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            pointerEvents: "none",
          }}
        >
          {currentScrollMonth}
        </Box>
      )}
    </>
  );
}
